// Every overlay (Dialog, the editor slide-over, ConfirmPopup) is a modal layer: while it is open
// the page behind does not scroll, and Tab and Shift+Tab stay inside the top-most layer.
import { useEffect, type RefObject } from 'react';

/**
 * The page's scroll lock, shared by every open layer: the first layer to open locks the page, the
 * last one to close unlocks it, in whatever order they close. Each layer used to remember the page's
 * setting and put it back itself, so a dialog and its confirm closing together (Reassign, Close
 * renewal) left the page locked with nothing open (Yehuda, Oct 27).
 */
type Body = { style: { overflow: string } };
let locks = 0;
let saved = '';
export function lockScroll(body: Body) {
  if (locks++ === 0) {
    saved = body.style.overflow;
    body.style.overflow = 'hidden';
  }
}
export function unlockScroll(body: Body) {
  if (locks === 0) return;
  if (--locks === 0) body.style.overflow = saved;
}

const focusable =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const stack: RefObject<HTMLElement | null>[] = [];

/** Whether this layer is the top-most one open, so Escape closes only that one. */
export const isTopLayer = (ref: RefObject<HTMLElement | null>) => stack.at(-1) === ref;

/**
 * Open layers that hold back the page's auto refresh: the popups and the Settings editors. The
 * Quick view does not: it shows the row the list reads again, so it refreshes with it (Codex on 292).
 */
let holding = 0;
/** Whether a popup or an editor is open: the page's auto refresh waits until it closes. */
export const anyLayerOpen = () => holding > 0;

export function useModalLayer(
  ref: RefObject<HTMLElement | null>,
  { holdsRefresh = true }: { holdsRefresh?: boolean } = {},
) {
  useEffect(() => {
    stack.push(ref);
    lockScroll(document.body);
    if (holdsRefresh) holding++;

    const onKey = (e: KeyboardEvent) => {
      const box = ref.current;
      if (e.key !== 'Tab' || !box || stack.at(-1) !== ref) return;
      const items = [...box.querySelectorAll<HTMLElement>(focusable)].filter(
        (el) => el.getClientRects().length > 0,
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const inside = box.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      stack.splice(stack.indexOf(ref), 1);
      unlockScroll(document.body);
      if (holdsRefresh) holding--;
    };
  }, [ref, holdsRefresh]);
}
